export interface DashboardLayoutItem {i:string;x:number;y:number;w:number;h:number;minW?:number;minH?:number;}
/** Insert a row at the visible section without colliding with spanning neighbours. */
export function insertDashboardWidget(layout: DashboardLayoutItem[], item: DashboardLayoutItem, anchorId?:string) {
  if(layout.some(existing=>existing.i===item.i))return layout;
  let y=layout.find(existing=>existing.i===anchorId)?.y ?? layout.reduce((max,existing)=>Math.max(max,existing.y+existing.h),0);
  while(layout.some(existing=>existing.y<y&&existing.y+existing.h>y)) {
    y=Math.min(...layout.filter(existing=>existing.y<y&&existing.y+existing.h>y).map(existing=>existing.y));
  }
  return [...layout.map(existing=>existing.y>=y?{...existing,y:existing.y+item.h}:existing),{...item,x:0,y}];
}
/** Push only collisions down; intentional spaces above or beside a card remain. */
export function resolveDashboardCollisions<T extends DashboardLayoutItem>(layout: readonly T[]): T[] {
  const result=layout.map(item=>({...item}));
  const placed:T[]=[];
  for(const item of [...result].sort((a,b)=>a.y-b.y||a.x-b.x)){
    let collisions:T[];
    do {
      collisions=placed.filter(other=>item.x<other.x+other.w&&item.x+item.w>other.x&&item.y<other.y+other.h&&item.y+item.h>other.y);
      if(collisions.length)item.y=Math.max(...collisions.map(other=>other.y+other.h));
    }while(collisions.length);
    placed.push(item);
  }
  return result;
}

/** A drag exchanges positions instead of pushing unrelated rows out of the way.
 * Keep each widget's size. If neither exchange nor a free drop fits, restore the
 * original layout rather than cascading changes through the dashboard.
 */
export function placeDashboardWidget<T extends DashboardLayoutItem>(before: readonly T[], moved: DashboardLayoutItem, cols: number): {layout:T[];rejected:boolean} {
  const original=before.find(item=>item.i===moved.i);
  const copy=()=>before.map(item=>({...item}));
  if(!original)return {layout:copy(),rejected:true};
  const intersects=(a:DashboardLayoutItem,b:DashboardLayoutItem)=>a.x<b.x+b.w&&a.x+a.w>b.x&&a.y<b.y+b.h&&a.y+a.h>b.y;
  const destination={...original,x:moved.x,y:moved.y};
  const targets=before.filter(item=>item.i!==original.i&&intersects(destination,item)).sort((a,b)=>{
    const overlap=(other:DashboardLayoutItem)=>(Math.min(destination.x+destination.w,other.x+other.w)-Math.max(destination.x,other.x))*(Math.min(destination.y+destination.h,other.y+other.h)-Math.max(destination.y,other.y));
    return overlap(b)-overlap(a)||a.i.localeCompare(b.i);
  });
  const target=targets[0];
  const layout=before.map(item=>item.i===original.i?{...item,x:target?.x??destination.x,y:target?.y??destination.y}:item.i===target?.i?{...item,x:original.x,y:original.y}:{...item});
  const changed=layout.filter(item=>item.i===original.i||item.i===target?.i);
  const invalid=changed.some(item=>item.x<0||item.y<0||item.x+item.w>cols||layout.some(other=>other.i!==item.i&&intersects(item,other)));
  return invalid?{layout:copy(),rejected:true}:{layout,rejected:false};
}
